'use client';

import { useEffect, useRef, type ReactElement } from 'react';
import { useTealium } from '@fshr/context/tealium-context';
import { ytReady, isPlaying, type YouTubePlayer } from './youtube-api';

export type Props = {
	videoId: string;
	title: string;
	shouldPlay: boolean;
	description?: string;
	interactive?: boolean;
	/**
	 * Whether shouldPlay becoming true should start playback itself. Off by
	 * default so an interactive embed (native YouTube controls) only plays on
	 * an actual user click there — shouldPlay going false still always pauses,
	 * regardless of this flag, so navigating away stops playback either way.
	 */
	autoplay?: boolean;
	onUnavailable: () => void;
};

export default function YouTubeEmbed({
	videoId,
	title,
	shouldPlay,
	description,
	interactive = false,
	autoplay = false,
	onUnavailable,
}: Props): ReactElement {
	const { onYouTubePlayerReady } = useTealium();
	const hostRef = useRef<HTMLDivElement>(null);
	const playerRef = useRef<YouTubePlayer | null>(null);
	const shouldPlayRef = useRef(shouldPlay);
	const autoplayRef = useRef(autoplay);
	// Not in the mount effect's deps: onYouTubePlayerReady is a fresh function
	// every TealiumProvider render, and re-running that effect on every render
	// would tear down and recreate the player for no reason.
	const onYouTubePlayerReadyRef = useRef(onYouTubePlayerReady);

	shouldPlayRef.current = shouldPlay;
	autoplayRef.current = autoplay;
	onYouTubePlayerReadyRef.current = onYouTubePlayerReady;

	useEffect(() => {
		const host = hostRef.current;
		if (!host) return;

		// YT.Player replaces the element it is handed with an iframe, so give it a
		// child React never renders into rather than the host itself.
		const mount = document.createElement('div');
		mount.style.width = '100%';
		mount.style.height = '100%';
		host.appendChild(mount);

		ytReady(yt => {
			playerRef.current = new yt.Player(mount, {
				host: 'https://www.youtube-nocookie.com',
				videoId,
				width: '100%',
				height: '100%',
				playerVars: {
					enablejsapi: 1,
					origin: window.location.origin,
					rel: 0,
					// The card drives playback from its own control, and browsers refuse
					// unmuted autoplay — same reason the MP4 element is muted.
					controls: interactive ? 1 : 0,
					disablekb: interactive ? 0 : 1,
					mute: interactive ? 0 : 1,
					playsinline: 1,
				},
				events: {
					// The clock does not wait for the player, so a slide can already be
					// asking to play by the time the API resolves. Catch up on arrival.
					onReady: (event: { target: YouTubePlayer }) => {
						onYouTubePlayerReadyRef.current(event, {});
						if (shouldPlayRef.current && autoplayRef.current) {
							playerRef.current?.playVideo();
						}
					},
					onError: onUnavailable,
				},
			});
		});

		return () => {
			playerRef.current?.destroy();
			playerRef.current = null;
			host.replaceChildren();
		};
	}, [videoId, interactive, onUnavailable]);

	useEffect(() => {
		const player = playerRef.current;
		// No player yet means the API has not resolved (or will not, in China). The
		// call is dropped and the poster stays up — onReady picks up the slack.
		if (!player) return;

		if (shouldPlay) {
			if (autoplay) {
				player.playVideo();
			}
		} else if (isPlaying(player)) {
			player.pauseVideo();
		}
	}, [shouldPlay, autoplay]);

	return (
		<>
			<div
				ref={hostRef}
				className={`absolute inset-0 h-full w-full${interactive ? '' : ' pointer-events-none'}`}
				aria-labelledby={`YouTubeEmbed-description-${videoId}`}
			/>
			<p id={`YouTubeEmbed-description-${videoId}`} className="hidden">
				{description ?? title}
			</p>
		</>
	);
}
